import { Action, createAction, handleActions } from "redux-actions";
import {
  GetPolizasMultiramaResponse,
  GetRamasResponse,
  PolizaMultiramaResponse,
} from "~api/types";
import { OptionKV } from "~styled/selector/Selector";
import {
  Dispatch,
  FiltroValue,
  GetState,
  Services,
  TakeValue,
} from "../../types";

interface ListadoPolizasMultiramaState {
  isLoading: boolean;
  failed: boolean;
  totalRegistros: number;
  polizas: PolizaMultiramaResponse[] | null;
  ramasFilter: OptionKV<string>[] | null;
  ramasOptions: Record<string, string>;
  polizaAnulada: boolean;
  loadingError?: boolean;
}

interface GetPolizasMultiramaParams {
  skip: number;
  take: TakeValue;
  searchCriteria?: string;
  compania?: FiltroValue;
  rama?: FiltroValue;
  estado?: FiltroValue;
  fechaVigenciaDesde?: Date;
  fechaVigenciaHasta?: Date;
}

const closeSuccessAnularPopUp = createAction("CLOSE_SUCCESS_ANULAR_POPUP");

const getPolizasMultiramaInited = createAction(
  "OBTENER_POLIZASMULTIRAMA_INITED"
);

const getPolizasMultiramaCompleted = createAction(
  "OBTENER_POLIZASMULTIRAMA_COMPLETED",
  (response: GetPolizasMultiramaResponse | null | undefined) => {
    let polizas: PolizaMultiramaResponse[] = [];
    let totalRegistros = 0;

    if (response != null) {
      polizas = response.polizas;
      totalRegistros = response.totalRegistros;
    }

    return { polizas, totalRegistros };
  }
);

const getPolizasMultiramaFailed = createAction(
  "OBTENER_POLIZASMULTIRAMA_FAILED",
  (error: unknown) => ({
    error,
  })
);

const getPolizasMultirama = ({
  skip,
  take,
  searchCriteria,
  compania,
  rama,
  estado,
  fechaVigenciaDesde,
  fechaVigenciaHasta,
}: GetPolizasMultiramaParams) => {
  return async (dispatch: Dispatch, getState: GetState, services: Services) => {
    try {
      dispatch(getPolizasMultiramaInited());

      const data = {
        skip,
        take: Number(take),
        searchCriteria,
        compania: compania ? String(compania) : undefined,
        rama: rama ? String(rama) : undefined,
        estado: estado ? String(estado) : undefined,
        fechaVigenciaDesde,
        fechaVigenciaHasta,
      };

      const response = await services.api
        .polizasMultirama()
        .getPolizasMultirama(data);

      dispatch(getPolizasMultiramaCompleted(response));
    } catch (error) {
      console.log(error);
      dispatch(getPolizasMultiramaFailed(error));
    }
  };
};

const getRamasInited = createAction("OBTENER_RAMAS_INITED");

const getRamas = () => {
  return async (dispatch: Dispatch, getState: GetState, services: Services) => {
    try {
      dispatch(getRamasInited());

      const response = await services.api.polizasMultirama().getRamas();

      dispatch(getRamasCompleted(response));
    } catch (error) {
      console.error(error);
      dispatch(getRamasFailed(error));
    }
  };
};

const getRamasCompleted = createAction(
  "OBTENER_RAMAS_COMPLETED",
  (response: GetRamasResponse) => ({ ramas: response })
);

const getRamasFailed = createAction(
  "OBTENER_RAMAS_FAILED",
  (error: unknown) => ({
    error,
  })
);

const patchAnularPolizaInited = createAction("ANULAR_POLIZA_INITED");

const patchAnularPolizaCompleted = createAction("ANULAR_POLIZA_COMPLETED");

const patchAnularPolizaFailed = createAction(
  "ANULAR_POLIZA_FAILED",
  (error: unknown) => ({
    error,
  })
);

const patchAnularPoliza = (
  polizaId: string | number,
  motivo: string | number,
  fechaAnulacion: string
) => {
  return async (dispatch: Dispatch, getState: GetState, services: Services) => {
    try {
      dispatch(patchAnularPolizaInited());

      const data = {
        motivo,
        fechaAnulacion,
      };

      await services.api
        .polizasMultirama()
        .patchAnularPoliza(String(polizaId), data);

      dispatch(patchAnularPolizaCompleted());
    } catch (error) {
      console.error(error);
      dispatch(patchAnularPolizaFailed(error));
    }
  };
};

const setInitialState = createAction("SET_POLIZAS_INITIAL_STATE");

const listadoPolizasMultiramaInitialState: ListadoPolizasMultiramaState = {
  isLoading: false,
  failed: false,
  totalRegistros: 0,
  polizas: null,
  ramasFilter: null,
  ramasOptions: {},
  polizaAnulada: false,
};

const listadoPolizaMultiramaReducer = handleActions<
  ListadoPolizasMultiramaState,
  any
>(
  {
    [setInitialState.toString()]: (): ListadoPolizasMultiramaState => {
      return {
        ...listadoPolizasMultiramaInitialState,
      };
    },
    [getPolizasMultiramaInited.toString()]: (
      state: ListadoPolizasMultiramaState
    ): ListadoPolizasMultiramaState => {
      return {
        ...state,
        totalRegistros: 0,
        isLoading: true,
      };
    },
    [getPolizasMultiramaCompleted.toString()]: (
      state: ListadoPolizasMultiramaState,
      action: Action<{
        polizas: PolizaMultiramaResponse[];
        totalRegistros: number;
      }>
    ): ListadoPolizasMultiramaState => {
      return {
        ...state,
        polizas: action.payload.polizas,
        totalRegistros: action.payload.totalRegistros,
        isLoading: false,
        failed: false,
      };
    },
    [getPolizasMultiramaFailed.toString()]: (
      state: ListadoPolizasMultiramaState
    ): ListadoPolizasMultiramaState => {
      return {
        ...state,
        isLoading: false,
        failed: true,
      };
    },
    [getRamasInited.toString()]: (
      state: ListadoPolizasMultiramaState
    ): ListadoPolizasMultiramaState => {
      return {
        ...state,
      };
    },
    [getRamasCompleted.toString()]: (
      state: ListadoPolizasMultiramaState,
      action: Action<{ ramas: GetRamasResponse }>
    ): ListadoPolizasMultiramaState => {
      let ramasFilter: OptionKV<string>[] = action.payload.ramas.ramas.map(
        (rama) => ({
          label: rama.nombre,
          value: rama.id,
        })
      );

      ramasFilter.unshift({ label: "Todos", value: "" });

      let ramasOptions: Record<string, string> = {};
      ramasOptions[""] = "Todos";

      action.payload.ramas.ramas.forEach((rama) => {
        ramasOptions[rama.id] = rama.nombre;
      });

      return {
        ...state,
        ramasFilter: ramasFilter,
        ramasOptions: ramasOptions,
        failed: false,
      };
    },
    [getRamasFailed.toString()]: (
      state: ListadoPolizasMultiramaState
    ): ListadoPolizasMultiramaState => {
      return {
        ...state,
        loadingError: true,
      };
    },
    [patchAnularPolizaInited.toString()]: (
      state: ListadoPolizasMultiramaState
    ): ListadoPolizasMultiramaState => {
      return {
        ...state,
        polizaAnulada: false,
        isLoading: true,
      };
    },
    [patchAnularPolizaCompleted.toString()]: (
      state: ListadoPolizasMultiramaState
    ): ListadoPolizasMultiramaState => {
      return {
        ...state,
        polizaAnulada: true,
        isLoading: false,
      };
    },
    [patchAnularPolizaFailed.toString()]: (
      state: ListadoPolizasMultiramaState
    ): ListadoPolizasMultiramaState => {
      return {
        ...state,
        polizaAnulada: false,
        isLoading: false,
      };
    },
    [closeSuccessAnularPopUp.toString()]: (
      state: ListadoPolizasMultiramaState
    ): ListadoPolizasMultiramaState => {
      return {
        ...state,
        polizaAnulada: false,
      };
    },
  },
  listadoPolizasMultiramaInitialState
);

export { listadoPolizaMultiramaReducer };

export {
  getPolizasMultirama,
  getRamas,
  patchAnularPoliza,
  closeSuccessAnularPopUp,
};
